/**
 * Trends: a metric over time, with dose events and a before/after comparison.
 *
 * This module answers "what did the numbers do" and stops there. It computes
 * rolling means (daily health data is noisy; a raw daily line misleads),
 * plain averages over two windows, and the count of days each average rests
 * on. It does not compute significance, effect sizes, or anything that would
 * dress an n=1 log with confounds everywhere up as a result. The interface
 * copy carries the same restraint.
 */
import { addDays, daysBetween, type Day } from '../lib/time';

export interface Point {
  day: Day;
  value: number | null;
}

/**
 * Trailing rolling mean over the last `window` calendar days, gaps ignored.
 * A point is null until at least `minCount` real values fall in its window,
 * so the line does not start confidently on one reading.
 */
export function rollingMean(points: Point[], window: number, minCount = 2): Point[] {
  if (window < 1) throw new RangeError('window must be at least 1');
  return points.map((p, i) => {
    const start = addDays(p.day, -(window - 1));
    const inWindow = points
      .slice(Math.max(0, i - window + 1), i + 1)
      .filter((q) => q.value !== null && q.day >= start)
      .map((q) => q.value as number);
    if (inWindow.length < minCount) return { day: p.day, value: null };
    return { day: p.day, value: inWindow.reduce((a, b) => a + b, 0) / inWindow.length };
  });
}

export interface WindowStat {
  from: Day;
  to: Day;
  /** Days in the window that actually had a value. */
  n: number;
  mean: number | null;
  min: number | null;
  max: number | null;
}

/** Plain average of the values on days in [from, to]. */
export function windowStat(points: Point[], from: Day, to: Day): WindowStat {
  const vals = points.filter((p) => p.value !== null && p.day >= from && p.day <= to).map((p) => p.value as number);
  if (!vals.length) return { from, to, n: 0, mean: null, min: null, max: null };
  return {
    from, to, n: vals.length,
    mean: vals.reduce((a, b) => a + b, 0) / vals.length,
    min: Math.min(...vals),
    max: Math.max(...vals),
  };
}

export interface Comparison {
  before: WindowStat;
  after: WindowStat;
  /** after.mean - before.mean, or null when either side is empty. */
  delta: number | null;
  /** True when both sides have enough days to be worth showing at all. */
  enough: boolean;
}

/** The minimum days per side before an average is shown. */
export const MIN_DAYS_PER_SIDE = 5;

/**
 * Compare the `windowDays` before `startDay` against the `windowDays` from
 * `startDay` on. The start day itself belongs to "after".
 */
export function compareAround(points: Point[], startDay: Day, windowDays: number): Comparison {
  if (windowDays < 1) throw new RangeError('windowDays must be at least 1');
  const before = windowStat(points, addDays(startDay, -windowDays), addDays(startDay, -1));
  const after = windowStat(points, startDay, addDays(startDay, windowDays - 1));
  const delta = before.mean !== null && after.mean !== null ? after.mean - before.mean : null;
  return { before, after, delta, enough: before.n >= MIN_DAYS_PER_SIDE && after.n >= MIN_DAYS_PER_SIDE };
}

/** Build a dense daily series over [from, to] from sparse rows. */
export function denseSeries(rows: { day: Day; value: number | undefined }[], from: Day, to: Day): Point[] {
  const byDay = new Map(rows.filter((r) => r.value !== undefined).map((r) => [r.day, r.value as number]));
  const out: Point[] = [];
  const n = daysBetween(from, to);
  for (let i = 0; i <= n; i++) {
    const day = addDays(from, i);
    out.push({ day, value: byDay.get(day) ?? null });
  }
  return out;
}

export interface DoseMark {
  day: Day;
  compoundId: string;
}

/** The day a compound first appears in the log, or null if never dosed. */
export function firstDoseDay(doses: DoseMark[], compoundId: string): Day | null {
  let first: Day | null = null;
  for (const d of doses) {
    if (d.compoundId !== compoundId) continue;
    if (first === null || d.day < first) first = d.day;
  }
  return first;
}

/** Map a series to SVG path data, breaking the line across nulls. */
export function pathFor(points: Point[], x: (i: number) => number, y: (v: number) => number): string {
  let d = '';
  let pen = false;
  points.forEach((p, i) => {
    if (p.value === null) { pen = false; return; }
    d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)} `;
    pen = true;
  });
  return d.trim();
}

/** Nice axis bounds with a little headroom, never collapsing to zero height. */
export function bounds(points: Point[]): { lo: number; hi: number } {
  const vals = points.filter((p) => p.value !== null).map((p) => p.value as number);
  if (!vals.length) return { lo: 0, hi: 1 };
  let lo = Math.min(...vals);
  let hi = Math.max(...vals);
  if (hi === lo) { lo -= 1; hi += 1; }
  const pad = (hi - lo) * 0.12;
  return { lo: lo - pad, hi: hi + pad };
}
